import { motion } from "framer-motion";
import { CheckCircle, Sparkles, Shield, Clock, Truck } from "lucide-react";
import { CTAButton } from "@/components/common/CTAButton";
import { SectionHeader } from "@/components/common/SectionHeader";
import { GlassCard } from "@/components/common/GlassCard";
import { cn } from "@/lib/utils";

const pricingTiers = [
  {
    name: "Essential Alterations",
    priceRange: "From ₹199",
    description: "Simple adjustments for everyday wear",
    features: [
      "Hemming (length adjustment)",
      "Taking in / letting out seams",
      "Waist adjustments",
      "Sleeve length alterations",
      "Basic repairs & patching",
    ],
  },
  {
    name: "Standard Tailoring",
    priceRange: "From ₹499",
    description: "Comprehensive alterations for the perfect fit",
    popular: true,
    features: [
      "All essential alterations",
      "Shoulder adjustments",
      "Dart modifications",
      "Lining repairs / replacement",
      "Zip & button replacement",
      "Blouse fitting",
    ],
  },
  {
    name: "Premium & Bridal",
    priceRange: "From ₹999",
    description: "Specialized care for precious garments",
    features: [
      "All standard alterations",
      "Complex structural changes",
      "Heavy embroidery handling",
      "Intricate beading / stonework",
      "Designer garment expertise",
      "Bridal wear alterations",
    ],
  },
];

const stitchingPrices = [
  {
    category: "Kurtis & Tops",
    items: [
      { name: "Cotton kurti (simple)", price: "₹799" },
      { name: "Designer kurti with work", price: "₹1,499" },
      { name: "Blouse (regular)", price: "₹549" },
      { name: "Designer blouse", price: "₹1,199" },
    ],
  },
  {
    category: "Suits & Dresses",
    items: [
      { name: "Salwar suit (basic)", price: "₹1,499" },
      { name: "Salwar suit (designer)", price: "₹2,999" },
      { name: "Western dress", price: "₹1,999" },
      { name: "Lehenga stitching", price: "₹4,999" },
    ],
  },
];

const inclusions = [
  { icon: Truck, label: "Free doorstep pickup & delivery" },
  { icon: Shield, label: "Professional measurements" },
  { icon: Clock, label: "Real-time order tracking" },
  { icon: CheckCircle, label: "Quality assurance checks" },
];

export default function PricingPage() {
  return (
    <div className="pt-20">
      <section className="zaklyn-section">
        <div className="zaklyn-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center mb-16"
          >
            <h1 className="zaklyn-heading-display text-foreground mb-6">
              Transparent Pricing
            </h1>
            <p className="zaklyn-body-lg">
              No hidden fees, no surprises. You approve every quote before work begins.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <GlassCard
                  className={cn(
                    "h-full p-6 md:p-8 relative overflow-hidden",
                    tier.popular && "ring-2 ring-primary"
                  )}
                >
                  {tier.popular && (
                    <div className="absolute top-0 right-0">
                      <div className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-bl-xl">
                        Most Popular
                      </div>
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                      {tier.name}
                    </h3>
                    <div className="font-display text-3xl font-bold text-primary mb-2">
                      {tier.priceRange}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {tier.description}
                    </p>
                  </div>
                  <ul className="space-y-3">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </GlassCard>
              </motion.div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-16">
            {inclusions.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <span className="text-sm font-medium text-foreground">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="zaklyn-section bg-secondary/30">
        <div className="zaklyn-container">
          <SectionHeader
            title="Custom Stitching"
            subtitle="Made-to-measure garments crafted to your specifications"
          />

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {stitchingPrices.map((category, index) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <GlassCard className="p-6 md:p-8">
                  <h3 className="font-display text-xl font-semibold text-foreground mb-6">
                    {category.category}
                  </h3>
                  <ul className="space-y-4">
                    {category.items.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-center justify-between pb-3 border-b border-border/50 last:border-0 last:pb-0"
                      >
                        <span className="text-foreground">{item.name}</span>
                        <span className="font-semibold text-primary">{item.price}</span>
                      </li>
                    ))}
                  </ul>
                </GlassCard>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center text-sm text-muted-foreground mt-8"
          >
            * Fabric cost not included. Final price depends on design complexity and embellishments.
          </motion.p>
        </div>
      </section>

      <section className="zaklyn-section">
        <div className="zaklyn-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <GlassCard className="p-8 md:p-12 max-w-3xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">
                    How Pricing Works
                  </h3>
                  <ul className="space-y-3 text-foreground">
                    <li className="flex items-start gap-3">
                      <span className="font-semibold text-primary">1.</span>
                      <span>Exact pricing provided after our stylist inspects your garment</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-semibold text-primary">2.</span>
                      <span>You receive a detailed quote with itemized breakdown</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-semibold text-primary">3.</span>
                      <span>Work begins only after your approval—no commitment until then</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="font-semibold text-primary">4.</span>
                      <span>Free re-alterations within 7 days if you're not satisfied</span>
                    </li>
                  </ul>
                  <div className="mt-6 p-4 bg-accent/50 rounded-xl">
                    <p className="text-sm text-foreground">
                      <strong>Bulk orders (5+ garments):</strong> Enjoy special pricing. Contact us for a custom quote.
                    </p>
                  </div>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </section>

      <section className="zaklyn-section bg-secondary/30">
        <div className="zaklyn-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="zaklyn-heading-2 text-foreground mb-4">
              Ready to Get Started?
            </h2>
            <p className="zaklyn-body mb-8">
              Schedule a pickup and receive your personalized quote. No commitment until you approve.
            </p>
            <CTAButton href="/customer/measurements/book" showArrow>
              Book Your Appointment
            </CTAButton>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
